import React, { useEffect, useState } from 'react';

import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput, IInputZone } from 'interfaces/IInput';
import { IInputTask, InputTaskCategory } from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';

import InputTasksHeader from './InputTasksHeader';
import { ALL_STEP_CATEGORY_KEY } from './stepCategoryFilters';
import { ProductionCropContext } from './stepFactory';
import TasksTableGrid from './TasksTableGrid';
import { useRememberedExpandedTasks } from './useRememberedExpandedTasks';

interface InputTasksProps {
  selectedInput: IInput;
  draftTasks: IInputTask[];
  // The plan's area and unit including unsaved edits, for new tasks.
  draftZone?: IInputZone | null;
  gUnits: GUnit[];
  productionCropContext: ProductionCropContext;
  userProfile?: PartialUserProfile;
  addDraftTask: (task: IInputTask) => void;
  updateDraftTask: (task: IInputTask) => void;
  removeDraftTask: (taskId: string) => void;
  schedulePendingTaskSave: (task: IInputTask) => void;
  schedulePendingNewTaskSave: (task: IInputTask) => void;
  schedulePendingTaskDelete: (taskId: string) => void;
}

const InputTasks: React.FC<InputTasksProps> = ({
  selectedInput,
  draftTasks,
  draftZone,
  gUnits,
  productionCropContext,
  userProfile,
  addDraftTask,
  updateDraftTask,
  removeDraftTask,
  schedulePendingTaskSave,
  schedulePendingNewTaskSave,
  schedulePendingTaskDelete,
}) => {
  const [activeStepCategoryKey, setActiveStepCategoryKey] = useState<string>(
    ALL_STEP_CATEGORY_KEY
  );
  const [activeTaskCategory, setActiveTaskCategory] =
    useState<InputTaskCategory>(InputTaskCategory.Plan);
  const [expandedTaskKeys, setExpandedTaskKeys] = useRememberedExpandedTasks(
    selectedInput.id,
    draftTasks
  );

  useEffect(() => {
    setActiveStepCategoryKey(ALL_STEP_CATEGORY_KEY);
    setActiveTaskCategory(InputTaskCategory.Plan);
  }, [selectedInput.id]);

  const handleExpandAll = () => {
    setExpandedTaskKeys(draftTasks.map((task) => task.taskId));
  };

  const handleCollapseAll = () => {
    setExpandedTaskKeys([]);
  };

  return (
    <>
      <InputTasksHeader
        activeStepCategoryKey={activeStepCategoryKey}
        onStepCategoryChange={setActiveStepCategoryKey}
        activeTaskCategory={activeTaskCategory}
        onTaskCategoryChange={setActiveTaskCategory}
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
      />
      <TasksTableGrid
        selectedInput={selectedInput}
        draftTasks={draftTasks}
        draftZone={draftZone}
        gUnits={gUnits}
        productionCropContext={productionCropContext}
        userProfile={userProfile}
        activeStepCategoryKey={activeStepCategoryKey}
        activeTaskCategory={activeTaskCategory}
        expandedTaskKeys={expandedTaskKeys}
        setExpandedTaskKeys={setExpandedTaskKeys}
        addDraftTask={addDraftTask}
        updateDraftTask={updateDraftTask}
        removeDraftTask={removeDraftTask}
        schedulePendingTaskSave={schedulePendingTaskSave}
        schedulePendingNewTaskSave={schedulePendingNewTaskSave}
        schedulePendingTaskDelete={schedulePendingTaskDelete}
      />
    </>
  );
};

export default InputTasks;
